import React, { Component, Fragment } from 'react';
import b from 'b_';
import { RouteComponentProps, Link } from 'react-router-dom';
import { TextInput } from 'lego-on-react';
import { Location } from 'history';

import { SearchRequestParams } from '@interfaces/search-request-params';
import { AsyncFetchDataProps } from '@interfaces/async-fetch-data';
import ShowMore from '@components/pagination-wrapper/show-more';
import PaginationMeta from '@interfaces/pagination-meta';
import { SearchItem } from '@interfaces/search-page';
import { formatDate } from 'client/helpers/date';
import PageMeta from '@components/page-meta';
import Icon from '@components/icon/icon';

import { isExternalUrl } from 'client/helpers/isExternalUrl';
import * as actions from 'client/ducks/search/actions';
import { formatQuery, parseQuery } from 'client/helpers/url';

import SearchPageStub from './search.stub';
import './search.css';

export interface DispatchProps {
    fetchSearchPage(params: SearchRequestParams): void;
}

export interface StateProps {
    lastSearchValue?: string;
    meta: PaginationMeta;
    rows: SearchItem[];
    hasMore: boolean;
    isLoading: boolean;
}

type Props = StateProps & DispatchProps & RouteComponentProps<{}>;

interface State {
    searchValue: string;
    activeTab: string;
}

interface QueryParams {
    text: string;
    tab: string;
}

const tabsMap: { [key: string]: any } = {
    all: 'All',
    news: 'News',
    publication: 'Publications',
    researcher: 'People'
};

const linkToEntityListMap: { [key: string]: any } = {
    researcher: { label: 'People', url: '/people' },
    publication: { label: 'Publication', url: '/publications' },
    news: { label: 'News', url: '/news' },
    outreach: { label: 'Outreach', url: '/outreach' }
};

const class_ = b.with('search-page');
const mobileTabsClass_ = b.with('mobile-tabs');

class SearchPage extends Component<Props, State> {
    public static getTypeByTab(tab?: string) {
        return tab === 'all' ? null : tab;
    }

    public static getQuery(location: Location): QueryParams {
        const query = parseQuery(location.search);

        return {
            tab: query.get('tab') || 'all',
            text: query.get('text') || ''
        };
    }

    public static asyncFetchData({ dispatch, location }: AsyncFetchDataProps) {
        const { tab, text } = SearchPage.getQuery(location);
        const type = SearchPage.getTypeByTab(tab);

        if (text) {
            dispatch(actions.fetchSearch({ text, type }));
        }
    }

    public constructor(props: Props) {
        super(props);

        const { tab, text } = SearchPage.getQuery(this.props.location);

        this.state = { searchValue: text, activeTab: tab };
    }

    public componentDidMount() {
        const { text: searchValue } = SearchPage.getQuery(this.props.location);

        if (searchValue && searchValue !== this.props.lastSearchValue) {
            this.onSearch();
        }
    }

    public shouldComponentUpdate() {
        return true;
    }

    public syncQuery(nextQueryParams: Partial<QueryParams>) {
        const queryParams = SearchPage.getQuery(this.props.location);
        const query = formatQuery({ ...queryParams, ...nextQueryParams } as any);

        this.props.history.push(`/search${query}`);

    }

    public onShowMoreClick = () => {
        const { pageNumber = 1 } = this.props.meta;

        this.onSearch(pageNumber + 1);
    };

    public onSearchInputChange = (searchValue: string) => {
        this.setState({ searchValue });
    };

    public onSearchInputKeyDown = (event: any) => {
        const { searchValue } = this.state;

        if (event.key === 'Enter' && searchValue.trim()) {
            this.onSearch();
            this.syncQuery({ text: searchValue });
        }
    };

    public onSearch = (page: number = 1) => {
        const { isLoading, fetchSearchPage } = this.props;
        const { searchValue: text, activeTab } = this.state;
        const type = SearchPage.getTypeByTab(activeTab);

        if (isLoading) {
            return;
        }

        fetchSearchPage({ text, type, page });
    };

    public onClickToSearchIcon = () => {
        this.onSearch();
    };

    public onFilterChange = (event: any) => {
        const activeTab = event.currentTarget.dataset.tab;

        if (this.props.isLoading) {
            return;
        }

        this.syncQuery({ tab: activeTab });
        this.setState({ activeTab }, () => this.onSearch());
    };

    public renderFilters() {
        const { activeTab } = this.state;

        return (
            <div className={mobileTabsClass_()}>
                {Object.entries(tabsMap).map(([tab, name]) => (
                    <span
                        key={tab}
                        data-tab={tab}
                        className={mobileTabsClass_('tab', { selected: tab === activeTab })}
                        onClick={this.onFilterChange}
                    >
                        {name}
                    </span>
                ))}
            </div>
        );
    }

    /* tslint:disable-next-line cyclomatic-complexity */
    public renderSearchItem(item: SearchItem, index: number) {
        const { id, url, type, title, matchedFragments, date } = item;

        // Удаляем хост, что бы поиск работал локально
        // Но для сущностей Outreach оставляем внешнюю ссылку
        const formattedUrl = type === 'outreach'
            ? url
            : url.replace(/https?:\/\/[a-z0-9-_.]+/, '');

        const baseLink = linkToEntityListMap[type];
        let formattedDate = date ? formatDate(date) : '';

        // Подсвечиваем найденные фрагменты текста
        let matched = matchedFragments && matchedFragments[0]
            .replace(/\\007\[/g, '<span>')
            .replace(/\\007]/g, '</span>');

        if (type === 'researcher') {
            formattedDate = '';
            matched = '';
        }

        return (
            <div key={`${type}-${id}`} className={class_('item')}>
                <div className={class_('title')}>
                    <span className={class_('index')}>{++index}.</span>
                    {isExternalUrl(formattedUrl) ? (
                        <a
                            href={formattedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={class_('name')}
                        >
                            {title}
                        </a>
                    ) : (
                        <Link className={class_('name')} to={formattedUrl}>
                            {title}
                        </Link>
                    )}
                </div>
                {Boolean(matched) && (
                    <div
                        className={class_('content')}
                        dangerouslySetInnerHTML={{ __html: matched }}
                    />
                )}
                <div className={class_('bottom')}>
                    {formattedDate}&nbsp;
                    <Link className={class_('bottom-link')} to={baseLink.url}>{baseLink.label}</Link>
                </div>
            </div>
        );
    }

    public renderSearchItems() {
        const { rows, hasMore, isLoading } = this.props;

        if (isLoading && !rows.length) {
            return (<SearchPageStub />);
        }

        if (!rows.length) {
            return (
                <div className={class_('not-found')}>
                    Not found
                </div>
            );
        }

        return (
            <Fragment>
                <div className="centering">
                    {rows.map((item, index) => this.renderSearchItem(item, index))}
                    {hasMore && (
                        <ShowMore
                            key="show-more"
                            onClick={this.onShowMoreClick}
                            isLoading={isLoading}
                        />
                    )}
                </div>
            </Fragment>
        );
    }

    public render() {
        return (
            <div className={class_()}>
                <PageMeta title="Search" />
                <div className={class_('header')}>
                    <div className={class_('header-content')}>
                        <div className={class_('input')}>
                            <TextInput
                                theme="normal"
                                size="n"
                                hasClear
                                disabled={this.props.isLoading}
                                onChange={this.onSearchInputChange}
                                onKeyDown={this.onSearchInputKeyDown}
                                text={this.state.searchValue}
                            />
                            <div
                                className={class_('input-icon', { disabled: this.props.isLoading })}
                                onClick={this.onClickToSearchIcon}
                            >
                                <Icon size="l" type="search" />
                            </div>
                        </div>
                        {this.renderFilters()}
                    </div>
                </div>
                {this.renderSearchItems()}
            </div>
        );
    }
}

export default SearchPage;
